import { parseForecastUnits } from "./rsoSalesForecastHelpers";
import { materialCatalogSignature } from "./rsoMaterialForecastHelpers";

const MAX_PAGES = 100;
const text = (value) => String(value ?? "").trim();
const hasPropertyId = (property) =>
  /^\d+$/.test(text(property?.contactInfoId)) &&
  Number.isSafeInteger(Number(property.contactInfoId)) &&
  Number(property.contactInfoId) > 0;
const isNotApplicable = (property) => property?.isManualException !== true &&
  (text(property?.rsoScope).toLowerCase() === "not_applicable" ||
  text(property?.submissionStatus).toLowerCase() === "not_applicable");
// The API owns assignment and IsRecurringStock qualification. The client only
// honors its explicit scope/permission markers and must not reclassify a
// returned property by inspecting display-detail material rows.
export const isForecastPropertyEligible = (property) => {
  const scope = text(property?.rsoScope).toLowerCase();
  const expectedScope = property?.isManualException === true ? "not_applicable" : "rso";
  return !isNotApplicable(property) && property?.isEditable === true && hasPropertyId(property) && scope === expectedScope;
};
export const isVisibleSavedManualException = (property) => property?.isPersistedManualException === true &&
  property?.isManualException === true && hasPropertyId(property) &&
  text(property?.rsoScope).toLowerCase() === "not_applicable";
const isReady = isForecastPropertyEligible;
const NOT_APPLICABLE_MESSAGE = "No RSO forecast needed. This property does not use recurring special-order materials. No setup changes are needed.";
const loadError = () =>
  new Error("The property list changed or could not be loaded completely. Please reload and try again.");
const addableSignature = (properties) => JSON.stringify(Array.isArray(properties) ? properties : []);

const SETUP_CATEGORIES = [
  { key: "multiple_layouts", label: "Floorplan decision needed",
    action: "More than one default floorplan is set up. The team must decide which floorplan applies or how to split the units before forecasting. Do not guess the split." },
  { key: "missing_layout", label: "Default floorplan missing",
    action: "No default floorplan is set up. Ask your support team to confirm whether recurring special-order forecasting applies and, if it does, add the correct floorplan." },
  { key: "recurring_materials", label: "Check recurring-order eligibility",
    action: "No materials are marked for recurring orders on this floorplan. Ask the team whether this property uses recurring special-order materials. Only mark the correct materials if it does; not every assigned property needs this forecast." },
  { key: "unknown_rso_classification", label: "Confirm RSO material settings",
    action: "NANCY does not know whether every material is RSO. Ask your administrator to confirm the RSO material settings; do not guess." },
  { key: "quantities", label: "Check material quantities",
    action: "The floorplan's recurring-order materials or quantities could not be calculated safely. Ask your support team to check the quantities and product IDs." },
  { key: "units", label: "Check material units",
    action: "The material units do not match a supported unit of measure. Ask your support team to confirm and correct the units; do not guess a conversion." },
  { key: "product_mapping", label: "Match products and store",
    action: "A material needs a matching active product and store in NANCY. Ask your support team to check the product setup." },
  { key: "other", label: "Other checks",
    action: "Review each property's message with your support team. Confirm whether recurring special-order forecasting applies before changing its setup." },
];
const setupCategory = (key, action) => {
  const category = SETUP_CATEGORIES.find((item) => item.key === key);
  return { ...category, ...(action ? { action } : {}) };
};

// Search and counts must cover the entire assignment list, never just page one.
// Fail the whole read if paging changes rather than display an incomplete list.
export const loadAllForecastPages = async (fetchPage) => {
  let first;
  const properties = [];
  const ids = new Set();
  for (let page = 1; page <= (first?.totalPages || 1); page += 1) {
    const payload = await fetchPage(page);
    if (
      !payload || !Array.isArray(payload.properties) ||
      !Number.isInteger(payload.page) || payload.page !== page ||
      !Number.isInteger(payload.pageSize) || payload.pageSize < 1 ||
      !Number.isInteger(payload.totalCount) || payload.totalCount < 0 ||
      !Number.isInteger(payload.totalPages) || payload.totalPages < 1 ||
      payload.totalPages > MAX_PAGES ||
      payload.totalPages !== Math.max(1, Math.ceil(payload.totalCount / payload.pageSize))
    ) throw loadError();
    if (!first) first = payload;
    if (
      payload.totalCount !== first.totalCount || payload.totalPages !== first.totalPages ||
      payload.pageSize !== first.pageSize || payload.forecastMonth !== first.forecastMonth ||
      payload.salespersonName !== first.salespersonName ||
      materialCatalogSignature(payload.materialOptions) !== materialCatalogSignature(first.materialOptions) ||
      addableSignature(payload.addableProperties) !== addableSignature(first.addableProperties) ||
      payload.properties.length !== Math.min(payload.pageSize, payload.totalCount - (page - 1) * payload.pageSize)
    ) throw loadError();
    payload.properties.forEach((property) => {
      if (!property || typeof property !== "object") throw loadError();
      if (text(property.contactInfoId)) {
        const id = hasPropertyId(property)
          ? String(Number(property.contactInfoId)) : text(property.contactInfoId);
        if (ids.has(id)) throw loadError();
        ids.add(id);
      }
      properties.push(property);
    });
  }
  if (properties.length !== first.totalCount) throw loadError();
  const addableIds = new Set();
  if (first.addableProperties.some((property) => {
    const id = hasPropertyId(property) ? String(Number(property.contactInfoId)) : "";
    if (!id || !isForecastPropertyEligible(property) || addableIds.has(id) || ids.has(id)) return true;
    addableIds.add(id);
    return false;
  })) throw loadError();
  return { ...first, properties, totalCount: properties.length, page: 1,
    pageSize: Math.max(1, properties.length), totalPages: 1 };
};

// These categories explain the server's decision; they never make a property editable.
// An assigned property without recurring materials is not necessarily missing setup.
export const getForecastSetupCategory = (property) => {
  if (isNotApplicable(property)) return null;
  if (!hasPropertyId(property)) return setupCategory("other", "This property needs a valid NANCY property ID. Ask your support team to check its setup.");
  if (isReady(property)) return null;
  const reason = text(property?.submissionStatus).toLowerCase();
  if (reason.includes("month closed") || reason.includes("period closed")) {
    return setupCategory("other", "This month is closed. Choose an open month to enter a forecast.");
  }
  if (reason.includes("exactly one default layout") || reason.includes("floorplan mix") || reason.includes("multiple layout")) {
    const layouts = property?.layouts;
    if (Array.isArray(layouts) && layouts.length === 0) {
      return setupCategory("missing_layout");
    }
    if ((Array.isArray(layouts) && layouts.length > 1) || reason.includes("multiple layout")) {
      return setupCategory("multiple_layouts");
    }
    return setupCategory("other", "The default floorplan setup is incomplete. Ask your support team to check it before forecasting.");
  }
  if (reason.includes("no default layout") || reason.includes("missing default layout") || reason.includes("no default floorplan")) {
    return setupCategory("missing_layout");
  }
  if (reason.includes("no classified rso materials") || reason.includes("no recurring materials")) {
    return setupCategory("recurring_materials");
  }
  if (reason.includes("unknown rso classification") ||
      /(?:^|:\s*)Default layout has materials with unknown RSO classification\.?$/i.test(text(property?.setupMessage))) {
    return setupCategory("unknown_rso_classification");
  }
  if (reason.includes("calculate") || reason.includes("calculation") || reason.includes("invalid rso material or quantity")) {
    return setupCategory("quantities");
  }
  if (reason.includes("unsupported") || reason.includes("conflicting units") || reason.includes("incompatible unit")) {
    return setupCategory("units");
  }
  if (reason.includes("mapping") || reason.includes("mapped") || reason.includes("catalog") || reason.includes("store mismatch")) {
    return setupCategory("product_mapping");
  }
  return setupCategory("other", "This property is not available for forecasting yet. Ask your support team whether recurring special-order forecasting applies and what needs checking.");
};

export const getForecastSetupMessage = (property) => isNotApplicable(property)
  ? NOT_APPLICABLE_MESSAGE : getForecastSetupCategory(property)?.action || "";

const SAFE_FLOORPLAN_REVIEW_MESSAGES = new Set([
  "The floorplans changed while you were editing. Discard this property's draft and reload before entering its counts again.",
]);
const SAVED_SPLIT_REVIEW_MESSAGE = "The saved total has no reliable unit-type breakdown. Enter the counts you want to save. Blank types will be saved as 0; the reviewed counts will replace the old total.";
const OLD_SPLIT_REVIEW_MESSAGES = new Set([
  "The saved total needs a floorplan breakdown. Enter a count for every current floorplan before saving again.",
  "Enter every floorplan count.",
]);
const SETUP_DETAIL_TRANSLATIONS = [
  ["This saved forecast is view only because an RSO replacement is no longer available for every flooring material\\. Ask your support team to update the material options",
    "This forecast is still saved, but its RSO material choices have changed, so it is view only. Ask your support team to update the available RSO materials."],
  ["Default layout has materials with unknown RSO classification",
    "NANCY does not know whether every material is RSO. Ask your administrator to confirm the RSO material settings; do not guess."],
  ["(?:Default layout has no classified RSO materials|No recurring materials)",
    "No materials are marked for repeat orders. Ask your support team whether this unit type belongs in the forecast."],
  ["Nancy could not calculate the default layout",
    "NANCY could not calculate the materials for this floorplan. Ask your support team to check its setup."],
  ["(?:Default layout has an invalid RSO material or quantity|units and material per unit must be zero or more)",
    "The materials or their amounts need checking. Ask your support team to check this floorplan's materials and quantities."],
  ["(?:Default layout has an unsupported material unit|The same (?:layout|floorplan) product has conflicting units|product \\d+ has an unsupported NANCY unit|select a valid product unit|unit for product \\d+ must be [A-Za-z0-9]+)",
    "The flooring measurements need checking. Ask your support team to confirm the correct units, such as square feet or square yards."],
  ["(?:Default layout product needs Nancy catalog mapping|product \\d+ is unavailable or not in a production store|unknown products need a product name or SKU)",
    "A flooring product needs checking in NANCY. Ask your support team to confirm the product and store."],
  ["product \\d+ is not assigned to the property's store",
    "A flooring product is not assigned to this property's store. Ask your support team to check its product setup."],
  ["(?:Property has no default floorplans|No default layout|Missing default layout)",
    "This property has no default floorplan. Ask your support team to add the correct one if this property belongs in the forecast."],
  ["Property has invalid or duplicate floorplan IDs; update its default floorplans",
    "NANCY could not identify every floorplan. Ask your support team to check the default floorplan records."],
  ["(?:This forecast month is closed|Month closed|This month is closed)",
    "This month is closed. Choose an open month to enter a forecast."],
  ["(?:Floorplan counts produce a material total that is too large|units and material per unit produce a total that is too large)",
    "The calculated material amount is too large. Ask your support team to check the unit counts and material amounts."],
];

// The API names the first affected apartment type before its technical reason.
// Match the known reason at the end, not an arbitrary colon or semicolon inside
// the real apartment name. Never echo unrecognized backend internals to Sales.
export const getForecastSetupDetail = (property) => {
  if (isNotApplicable(property)) return "";
  const message = text(property?.setupMessage);
  if (!message || /^(null|undefined)$/i.test(message)) return "";
  if (SAFE_FLOORPLAN_REVIEW_MESSAGES.has(message)) return message;
  if (OLD_SPLIT_REVIEW_MESSAGES.has(message)) return SAVED_SPLIT_REVIEW_MESSAGE;
  for (const [reason, explanation] of SETUP_DETAIL_TRANSLATIONS) {
    const match = message.match(new RegExp(`^(?:(.+):\\s*)?(?:${reason})\\.?$`, "i"));
    if (match) return match[1] ? `${match[1].trim()}: ${explanation}` : explanation;
  }
  const layoutNames = (Array.isArray(property?.layouts) ? property.layouts : [])
    .filter(Boolean).flatMap((layout) => [layout.unitType, layout.floorplanName, layout.defaultFloorplanName])
    .map(text).filter(Boolean).sort((a, b) => b.length - a.length);
  const namedLayout = layoutNames.find((name) => message.toLowerCase().startsWith(`${name.toLowerCase()}:`));
  return namedLayout
    ? `${namedLayout}: Ask your support team to check this unit type's forecast setup.`
    : "Ask your support team to check this property's forecast setup.";
};

export const getForecastSetupSummary = (properties) => {
  const counts = new Map();
  (Array.isArray(properties) ? properties : []).filter(Boolean).forEach((property) => {
    const category = getForecastSetupCategory(property);
    if (category) counts.set(category.key, (counts.get(category.key) || 0) + 1);
  });
  return SETUP_CATEGORIES.filter((category) => counts.has(category.key))
    .map((category) => ({ ...category, count: counts.get(category.key) }));
};

export const getForecastStatus = (property) => {
  if (isNotApplicable(property)) {
    return {kind: "not_applicable", label: "Not applicable to RSO", description: NOT_APPLICABLE_MESSAGE, isSaved: false};
  }
  if (isVisibleSavedManualException(property) && property?.isEditable !== true) {
    return {kind:"saved_locked",label:"Saved · read only",
      description:"This one-month forecast is saved, but its current material setup no longer allows changes.",isSaved:true};
  }
  if (!isReady(property)) {
    return { kind: "setup", label: "Check RSO eligibility or setup", description: getForecastSetupMessage(property), isSaved: false };
  }
  const status = text(property.submissionStatus).toLowerCase();
  if (status === "floorplan_counts_need_review") {
    return {kind: "review", label: "Check the unit counts", description: SAVED_SPLIT_REVIEW_MESSAGE, isSaved: false};
  }
  const units = parseForecastUnits(property.forecastUnits);
  if (["submitted_pending", "submitted_pending_planning"].includes(status) && units.valid && units.answered) {
    return { kind: "saved", label: "Saved",
      description: "This forecast is saved. No order was placed.", isSaved: true };
  }
  if ((!status || status === "not_submitted") && !units.answered) {
    return { kind: "ready", label: "Ready to forecast",
      description: "Enter the number of units expected for this month.", isSaved: false };
  }
  return { kind: "unknown", label: "Check forecast status",
    description: "The latest planning status is not confirmed here. Check with your support team before assuming it was applied.", isSaved: false };
};

export const filterForecastProperties = (properties, { query = "", view = "all", setupCategory: selectedCategory = "all" } = {}) => {
  const search = text(query).toLowerCase();
  return (Array.isArray(properties) ? properties : []).filter((property) => {
    if (!property) return false;
    if (view === "ready" && !isReady(property) && !isVisibleSavedManualException(property)) return false;
    if (view === "setup" && (isReady(property) || isNotApplicable(property))) return false;
    if (selectedCategory && selectedCategory !== "all" && getForecastSetupCategory(property)?.key !== selectedCategory) return false;
    return !search || [property.propertyName, property.store, property.costarId]
      .some((value) => text(value).toLowerCase().includes(search));
  }).sort((a, b) => text(a.propertyName).localeCompare(text(b.propertyName), undefined, { sensitivity: "base" }) ||
    text(a.contactInfoId).localeCompare(text(b.contactInfoId), undefined, { numeric: true }));
};

export const getForecastCounts = (properties) => {
  const all = (Array.isArray(properties) ? properties : []).filter(Boolean);
  const ready = all.filter(isReady).length;
  const notApplicable = all.filter(isNotApplicable).length;
  return { all: all.length, ready, setup: all.length - ready - notApplicable, notApplicable,
    saved: all.filter((property) => getForecastStatus(property).isSaved).length };
};
